/**
 * JourneyVessel — the drop-and-slab pair.
 *
 * Physically the "same" vessel to the viewer, but implemented as two
 * meshes that share identical material and hand off during the
 * flatten/contract windows. The morph is a coordinated crossfade at a
 * matched screen position, not a mesh swap.
 *
 *   • dropMesh — a Sphere. Owns the drop, the travel, and the merge.
 *   • slabMesh — a RoundedBox with light corner radius. Owns the flat
 *                square glass tablet the guest journey lives on.
 *
 * The two share MeshTransmissionMaterial settings so the substance
 * reads as continuous. Only their opacity + scale schedule differs.
 *
 * This structure preserves the "one vessel" narrative visually while
 * giving us honest, edge-crisp geometry when the slab is flat.
 */
import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { RoundedBox, Sphere, MeshTransmissionMaterial } from "@react-three/drei";
import { phaseProgress, easeSettle } from "./LiquidGlassTransition";

// Shared material config so drop and slab read as the same substance.
const MATERIAL_PROPS = {
  transmission: 1,
  thickness: 0.7,
  roughness: 0.02,
  ior: 1.5,
  chromaticAberration: 0.04,
  anisotropy: 0.06,
  distortion: 0.15,
  distortionScale: 0.4,
  temporalDistortion: 0.04,
  attenuationDistance: 2.2,
  attenuationColor: "#f6f4ef",
  clearcoat: 0.85,
  clearcoatRoughness: 0.04,
  background: "#00000000",
};

function lerp(a, b, t) { return a + (b - a) * t; }
function lerp3(a, b, t) { return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]; }

export default function JourneyVessel({ transition }) {
  const dropRef = useRef();
  const slabRef = useRef();
  const dropMatRef = useRef();
  const slabMatRef = useRef();

  const cubeSurface = [transition.SCENE.cubeSurfaceX, 0, 0];
  const vesselPos   = transition.SCENE.vesselPosition;
  const dropScale   = transition.SCENE.dropScale;
  const slabScale   = transition.SCENE.slabScale;

  useFrame(() => {
    if (!dropRef.current || !slabRef.current) return;
    const now = performance.now();
    const p = phaseProgress(transition.phase, transition.phaseStartMs, now);
    const eased = easeSettle(p);

    // Defaults: both meshes invisible.
    let dropVisible = false, slabVisible = false;
    let dropPos = cubeSurface, slabPos = vesselPos;
    let dropSc = dropScale, slabSc = slabScale;
    let dropOpacity = 0, slabOpacity = 0;

    switch (transition.phase) {
      case "resting":
      case "fluidizing":
        // Nothing separated yet.
        break;

      case "separating":
        // Drop emerges from cube face.
        dropVisible = true;
        dropOpacity = eased;
        dropPos = cubeSurface;
        dropSc = [dropScale[0] * eased, dropScale[1] * eased, dropScale[2] * eased];
        break;

      case "traveling":
        dropVisible = true;
        dropOpacity = 1;
        dropPos = lerp3(cubeSurface, vesselPos, eased);
        dropSc = dropScale;
        break;

      case "flattening":
        // Coordinated handoff: drop shrinks + fades out; slab grows + fades in.
        // Both are pinned at the vessel position so the viewer reads it as
        // one continuous "settling."
        dropVisible = true;
        slabVisible = true;
        dropPos = vesselPos;
        slabPos = vesselPos;
        dropSc = [
          lerp(dropScale[0], dropScale[0] * 0.3, eased),
          lerp(dropScale[1], dropScale[1] * 0.3, eased),
          lerp(dropScale[2], dropScale[2] * 0.3, eased),
        ];
        // Slab starts small (matches the drop's footprint) and grows to full.
        slabSc = [
          lerp(dropScale[0], slabScale[0], eased),
          lerp(dropScale[1], slabScale[1], eased),
          lerp(dropScale[2], slabScale[2], eased),
        ];
        dropOpacity = Math.max(0, 1 - eased * 1.3);
        slabOpacity = Math.min(1, eased * 1.15);
        break;

      case "solidifying":
        slabVisible = true;
        slabPos = vesselPos;
        slabOpacity = 1;
        // Tiny settle overshoot (0.4%) that damps back to 1.
        const settle = 1 + 0.004 * Math.sin(eased * Math.PI);
        slabSc = [slabScale[0] * settle, slabScale[1] * settle, slabScale[2]];
        break;

      case "stable":
        slabVisible = true;
        slabPos = vesselPos;
        slabSc = slabScale;
        slabOpacity = 1;
        break;

      case "softening":
        slabVisible = true;
        slabPos = vesselPos;
        slabOpacity = 1;
        const wob = 0.006 * eased;
        slabSc = [
          slabScale[0] * (1 - wob),
          slabScale[1] * (1 + wob),
          slabScale[2] * (1 + wob * 3),
        ];
        break;

      case "contracting":
        // Reverse handoff: slab shrinks + fades out; drop grows + fades in.
        dropVisible = true;
        slabVisible = true;
        dropPos = vesselPos;
        slabPos = vesselPos;
        slabSc = [
          lerp(slabScale[0], dropScale[0], eased),
          lerp(slabScale[1], dropScale[1], eased),
          lerp(slabScale[2], dropScale[2], eased),
        ];
        dropSc = [
          lerp(dropScale[0] * 0.3, dropScale[0], eased),
          lerp(dropScale[1] * 0.3, dropScale[1], eased),
          lerp(dropScale[2] * 0.3, dropScale[2], eased),
        ];
        slabOpacity = Math.max(0, 1 - eased * 1.3);
        dropOpacity = Math.min(1, eased * 1.15);
        break;

      case "returning":
        dropVisible = true;
        dropOpacity = 1;
        dropPos = lerp3(vesselPos, cubeSurface, eased);
        dropSc = dropScale;
        break;

      case "merging":
        dropVisible = true;
        dropOpacity = 1 - eased;
        dropPos = cubeSurface;
        dropSc = [
          dropScale[0] * (1 - eased),
          dropScale[1] * (1 - eased),
          dropScale[2] * (1 - eased),
        ];
        break;

      default:
        break;
    }

    // Apply to drop.
    dropRef.current.visible = dropVisible;
    dropRef.current.position.set(dropPos[0], dropPos[1], dropPos[2]);
    dropRef.current.scale.set(dropSc[0], dropSc[1], dropSc[2]);
    if (dropMatRef.current) {
      dropMatRef.current.opacity = dropOpacity;
      dropMatRef.current.transparent = dropOpacity < 1;
    }

    // Apply to slab.
    slabRef.current.visible = slabVisible;
    slabRef.current.position.set(slabPos[0], slabPos[1], slabPos[2]);
    slabRef.current.scale.set(slabSc[0], slabSc[1], slabSc[2]);
    if (slabMatRef.current) {
      slabMatRef.current.opacity = slabOpacity;
      slabMatRef.current.transparent = slabOpacity < 1;
    }
  });

  return (
    <group>
      {/* Drop mesh — a Sphere. Owns pinch-off, travel, and merge. */}
      <Sphere
        ref={dropRef}
        args={[0.5, 48, 48]}
        visible={false}
      >
        <MeshTransmissionMaterial ref={dropMatRef} {...MATERIAL_PROPS} transparent />
      </Sphere>

      {/* Slab mesh — a RoundedBox with small corner radius. Reads as a
          two-dimensional square glass tablet when scaled flat. */}
      <RoundedBox
        ref={slabRef}
        args={[1, 1, 1]}
        radius={0.09}
        smoothness={5}
        creaseAngle={0.4}
        visible={false}
      >
        <MeshTransmissionMaterial ref={slabMatRef} {...MATERIAL_PROPS} transparent />
      </RoundedBox>
    </group>
  );
}
